// VAT region classification. Spec: docs/superpowers/specs/2026-09-25-dynamic-vat-design.md §1.
// CommonJS on purpose: the Express server requires this file and the client
// imports it; it must not import any ESM module.

// The 27 member states' VAT territories. Åland, the Canary Islands etc. are
// third territories and are NOT in this list.
const EU_VAT_AREA = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
];

const REGION = { FI: 'FI', AX: 'AX', EU: 'EU', NON_EU: 'NON_EU' };

// Åland postal codes are 22000–22999.
const ALAND_POSTCODE = /^22\d{3}$/;

const normalizeCountry = country =>
  typeof country === 'string' ? country.trim().toUpperCase() : '';

const isAlandPostcode = postalCode =>
  ALAND_POSTCODE.test(String(postalCode || '').replace(/\s+/g, ''));

// Country for VAT-law purposes: Åland is part of Finland under the Finnish
// VAT Act, so 'AX' folds into 'FI'. Use region() to tell them apart.
const vatCountry = party => {
  const country = normalizeCountry(party && party.country);
  return country === 'AX' ? 'FI' : country;
};

const region = party => {
  const country = normalizeCountry(party && party.country);
  if (country === 'AX') return REGION.AX;
  if (country === 'FI') return isAlandPostcode(party.postalCode) ? REGION.AX : REGION.FI;
  return EU_VAT_AREA.includes(country) ? REGION.EU : REGION.NON_EU;
};

const isInEuVatArea = r => r === REGION.FI || r === REGION.EU;

// A VAT number's prefix is the issuing country's ISO code, except that Greece
// issues EL. Åland has no numbers of its own: Finnish ones apply.
const VAT_PREFIX_BY_COUNTRY = { GR: 'EL', AX: 'FI' };
const vatPrefixForCountry = country => {
  const c = normalizeCountry(country);
  return VAT_PREFIX_BY_COUNTRY[c] || c;
};

const vatNumberPrefix = vatNumber => {
  const m = /^([A-Z]{2})/.exec(
    String(vatNumber || '')
      .replace(/[\s.\-]/g, '')
      .toUpperCase()
  );
  return m ? m[1] : null;
};

// Inside Finland, Åland and the EU VAT area a business's VAT number must be
// the billing country's own: a German number on a Finnish billing address
// would be priced as a Finnish sale while VIES checks a German company.
// Outside the EU VAT area the number is not an EU VAT number and is ignored.
const vatNumberMatchesCountry = (vatNumber, country) => {
  if (region({ country: country, postalCode: '' }) === REGION.NON_EU) return true;
  return vatNumberPrefix(vatNumber) === vatPrefixForCountry(country);
};

module.exports = {
  EU_VAT_AREA,
  REGION,
  region,
  vatCountry,
  normalizeCountry,
  isAlandPostcode,
  isInEuVatArea,
  vatPrefixForCountry,
  vatNumberMatchesCountry,
};
